import React, {useImperativeHandle, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import CardHeader from 'components/atoms/Cards/CardHeader/CardHeader';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Delete from '@mui/icons-material/Delete';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import StyledClientConnectionProfileCard
  from 'components/organisms/Cards/ClientConnectionProfileCard/ClientConnectionProfileCard.styles';

const ClientConnectionProfileCard = React.forwardRef((props, ref) => {
  const {
    card,
    action,
    onHide,
    connection,
    client,
    fieldData,
    isDialog,
    renderedContent,
    ...innerProps
  } = useComponentProps(props, 'ClientConnectionProfileCard', {
    static: ['isAddNew', 'isReadOnly', 'isEditing', 'isDeleted', 'isDialog'],
    children: ['content']
  });

  const innerRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const isAddNew = !utils.isDefined(connection?.connectionId);
  const isReadOnly = !card.card.editable;
  const isNewClient = !(client?.clientId >= 0);
  const isLoading = !utils.isDefined(connection?.name) || !utils.isDefined(connection?.type);

  const dialogControl = useDialogControl();

  useLayoutEffect(() => {
    const editing = card.state.isEditing ?? card.state.card.isEditing ?? card.state.dialog.isEditing;
    if (!isAddNew) {
      setIsEditing(editing);
    } else {
      if (!card.state.card.submitSuccess) { // wait for new card
        setIsEditing(editing);
      }
    }
  }, [card.state.isEditing, card.state.card.isEditing, card.state.dialog.isEditing, card.state.card.submitSuccess, isAddNew]);

  const handleDeleteClick = (e) => {
    const handleConfirm = () => {
      const connections = (client?.props?.connections ?? []).filter((c) => c.connectionId !== connection.connectionId);
      const connectionsValue = utils.uniqueArray(connections, 'connectionId', true);
      const field = {
        name: 'connections'
      };

      return utils.asPromiseCallback(card.card.onPatch)(field, connectionsValue)
        .then(() => {
          onHide?.(true);
          setIsDeleted(true);
        });
    }

    if (!isNewClient) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to remove this connection?"
                                        explanation="The connection will be removed from this client"
                                        challenge={connection.name}
                                        challengeLabel="connection name"
                                        onConfirm={handleConfirm}
                                        ConfirmButtonProps={{
                                          children: 'Remove connection',
                                          startIcon: <Icon icon={Delete} />,
                                          color: 'error'
                                        }}/>, true);
    } else {
      handleConfirm();
    }

    e.preventDefault();
  };

  const renderActions = () => {
    if (action && !isLoading) {
      return <React.Fragment>
        {!(isEditing || isAddNew || isReadOnly) ? <ActionIconButton className="ProfileCard-action"
                                                      variant="contained"
                                                      size="smaller"
                                                      color="error"
                                                      density="dense"
                                                      tabIndex={-1}
                                                      action={{
                                                        icon: <Icon icon={Delete} size="smaller" />,
                                                        tooltip: 'Remove connection'
                                                      }}
                                                      IconProps={{size: 'tiny'}}
                                                      onClick={handleDeleteClick} /> : null}
        <ActionIconButton className="ProfileCard-action"
                          tabIndex={-1}
                          action={action} density="dense"
                          size="smaller" variant="outlined"
                          IconProps={{size: 'tiny'}}/>
      </React.Fragment>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    isAddNew: isAddNew,
    isReadOnly: isReadOnly,
    isEditing: isEditing,
    isDeleted: isDeleted
  });

  return <StyledClientConnectionProfileCard ref={innerRef} {...innerProps}>
    {!(isAddNew || isDialog) ? <CardHeader className="ProfileCard-header"
                                           title=""
                                           action={renderActions()}/> : null}
    {renderedContent}
  </StyledClientConnectionProfileCard>
});

ClientConnectionProfileCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  card: PropTypes.object,
  action: PropTypes.object,
  onHide: PropTypes.func,
  connection: PropTypes.object,
  client: PropTypes.object,
  fieldData: PropTypes.object,
  isDialog: PropTypes.bool,
  renderedContent: PropTypes.any
};

export default ClientConnectionProfileCard;
